import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Reveal } from "./motion/Reveal";

export const ExhibitionGallery = ({ images }) => {
  const [open, setOpen] = useState(null);

  useEffect(() => {
    if (open === null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(null);
      if (e.key === "ArrowRight") setOpen((i) => (i + 1) % images.length);
      if (e.key === "ArrowLeft") setOpen((i) => (i - 1 + images.length) % images.length);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    if (window.__lenis) window.__lenis.stop();
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      if (window.__lenis) window.__lenis.start();
    };
  }, [open !== null, images.length]);

  if (!images || images.length === 0) return null;

  return (
    <section className="hairline-t px-5 py-16 lg:px-10 lg:py-24" data-testid="exhibition-gallery">
      <Reveal>
        <h2 className="eyebrow text-xs">Gallery</h2>
      </Reveal>
      <div className="mt-8 grid grid-cols-2 gap-4 lg:gap-6">
        {images.slice(0, 6).map((img, i) => (
          <Reveal key={i} delay={(i % 2) * 0.08}>
            <button
              type="button"
              data-testid={`gallery-tile-${i}`}
              onClick={() => setOpen(i)}
              aria-label={`Open image ${i + 1}${img.alt ? `: ${img.alt}` : ""}`}
              className="group block w-full overflow-hidden"
            >
              <img
                src={img.image}
                alt={img.alt || ""}
                loading="lazy"
                className="aspect-[4/3] w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]"
              />
            </button>
          </Reveal>
        ))}
      </div>

      <AnimatePresence>
        {open !== null && (
          <motion.div
            data-testid="gallery-overlay"
            className="fixed inset-0 z-[80] flex items-center justify-center bg-charcoal/95 p-5"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setOpen(null)}
            role="dialog"
            aria-modal="true"
            aria-label="Image viewer"
          >
            <motion.img
              key={open}
              src={images[open].image}
              alt={images[open].alt || ""}
              className="max-h-[88vh] max-w-[92vw] object-contain"
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
            />
            <button
              type="button"
              data-testid="gallery-overlay-close"
              aria-label="Close image viewer"
              onClick={() => setOpen(null)}
              className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full border border-offwhite text-offwhite transition-colors duration-300 hover:bg-offwhite hover:text-charcoal"
            >
              <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <line x1="2" y1="2" x2="14" y2="14" />
                <line x1="14" y1="2" x2="2" y2="14" />
              </svg>
            </button>
            <button
              type="button"
              data-testid="gallery-prev"
              aria-label="Previous image"
              onClick={(e) => {
                e.stopPropagation();
                setOpen((open - 1 + images.length) % images.length);
              }}
              className="absolute left-5 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-offwhite text-offwhite transition-colors duration-300 hover:bg-offwhite hover:text-charcoal"
            >
              <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="10 2 4 8 10 14" />
              </svg>
            </button>
            <button
              type="button"
              data-testid="gallery-next"
              aria-label="Next image"
              onClick={(e) => {
                e.stopPropagation();
                setOpen((open + 1) % images.length);
              }}
              className="absolute right-5 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-offwhite text-offwhite transition-colors duration-300 hover:bg-offwhite hover:text-charcoal"
            >
              <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 2 12 8 6 14" />
              </svg>
            </button>
            {images[open].alt && (
              <p className="absolute bottom-5 left-1/2 max-w-[80vw] -translate-x-1/2 text-center text-xs font-light text-offwhite/80">
                {images[open].alt}
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
